/**
 * 3D 数据大屏的 WebGL 场景（与 Vue 解耦：Vue 只负责挂载容器和喂数据）。
 *
 * 为什么自己包一层而不是用 Vue 封装库：Vue 2 时代的 three 封装基本停更，
 * 且会把 renderer/loop/dispose 藏进组件生命周期里，SPA 切换路由时最容易漏 dispose。
 * 这里把 renderer / 动画循环 / 资源释放收敛成一个类，页面只在
 * mounted 里 new、beforeDestroy 里 dispose，生命周期是显式且唯一的。
 *
 * 依赖版本锁定 three@0.137：该版本的 ESM 产物不含可选链等新语法，
 * webpack 4 自带的 acorn 才能解析；更高版本的 build 会直接编译报错。
 */
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'

// 与暗色仪器面板同源的低饱和语义色（禁止用纯红纯绿，见自绘组件配色规范）
const PALETTE = {
  chassis: 0x0a131c,
  seam: 0x16232f,
  brand: 0x0a76c6,
  brandLight: 0x38b6f0,
  chrome: 0x8fa5b8,
  pass: 0x4eae7f,
  warn: 0xd9a052,
  fail: 0xe05c52
}

const RING_RADIUS = 13.5 // 环形柱状统计所在半径
const MAX_BARS = 30 // 环形最多 30 根柱（30 天）：一天一根柱配一个日期刻度，60 根时刻度会挤成一串糊字
const LABEL_RADIUS = 16.6 // 日期刻度所在半径：压在柱外一点（柱体外沿 13.98），不跟柱体抢画面
const LABEL_W = 2.9 // 单张刻度的世界宽（30 根柱在 LABEL_RADIUS 上间隔约 3.48，留得开）
const LABEL_H = 0.725 // 画布 512×128，宽高比跟着走才不会拉变形
const GAS_COUNT = 520 // 气体分子粒子总数（用 drawRange 控制实际密度）
const GAS_TARGET = new THREE.Vector3(0, 4.8, 0) // 芯片振膜上方的吸附点（气体分子汇聚处）
const CHIP_Y = 4.05 // 芯片板所在高度，也是相机注视的中心
// 注视点往下压这么多，轮盘在画面里就整体上移这么多（大致每 1 单位约 23px：1.1 约 25px，1.8 约 41px，2.6 约 60px）
const VIEW_LIFT = 2.6

export default class DashboardScene {
  /**
   * @param {HTMLElement} container 画布挂载容器（需有宽高）
   * @param {Object} hooks { onSelectDay(index) }
   */
  constructor (container, hooks = {}) {
    this.container = container
    this.hooks = hooks
    this.clock = new THREE.Clock()
    this.raf = null
    this.disposed = false
    this.frameAcc = 0
    this.fps = 30 // 大屏常驻页面，30fps 足够顺滑，省下的是显存和风扇
    this.lastFrame = 0
    this.pointer = { down: null, moved: false }
    this.barPolar = [] // 每根柱的角度，供拾取和高亮复用
    this.highlightIndex = -1

    this._initRenderer()
    this._initScene()
    this._buildFloor()
    this._buildStars()
    this._buildChip()
    this._buildGas()
    this._buildBars()
    this._initComposer()
    this._bindEvents()

    this._resize()
    this._loop()
  }

  // ── 基础设施 ────────────────────────────────────────────────

  _initRenderer () {
    const { clientWidth: w, clientHeight: h } = this.container
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance'
    })
    // 设备像素比封顶 1.75：4K 屏上不封顶会让 bloom 的开销翻几倍
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75))
    // 这里必须让 three 自己写 canvas 的 style 宽高：canvas 是运行时插进容器的，
    // 拿不到 Vue 的 scoped 属性，页面里的 :deep(canvas) 只管 display/outline，
    // 尺寸一旦不同步就会按 pixelRatio 放大铺开，整个场景被裁到右下角
    this.renderer.setSize(w || 800, h || 600)
    this.renderer.setClearColor(PALETTE.chassis, 1)
    this.renderer.outputEncoding = THREE.sRGBEncoding
    this.container.appendChild(this.renderer.domElement)
  }

  _initScene () {
    const { clientWidth: w, clientHeight: h } = this.container
    this.scene = new THREE.Scene()
    // 指数雾：远处的星点和地面网格自然消隐，省掉一整层后期景深
    this.scene.fog = new THREE.FogExp2(PALETTE.chassis, 0.012)

    this.camera = new THREE.PerspectiveCamera(48, (w || 800) / (h || 600), 0.1, 400)
    this.camera.position.set(0, 11.5, 29)

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    // 上抬轮盘靠压低注视点而不是改 camera.position：OrbitControls.update() 每帧拿
    // offset = position - target 反推球坐标，再 position = target + offset 写回去，两式互为逆运算，
    // 相机位置其实没动，只是 lookAt 了一个更低的点 = 视线往下俯，画面里的轮盘就整体上移
    this.controls.target.set(0, CHIP_Y + 0.15 - VIEW_LIFT, 0)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.08
    this.controls.enablePan = false
    this.controls.minDistance = 18
    this.controls.maxDistance = 46
    this.controls.minPolarAngle = 0.62
    this.controls.maxPolarAngle = 1.42
    this.controls.rotateSpeed = 0.55
    this.controls.autoRotate = true
    this.controls.autoRotateSpeed = 0.28

    const hemi = new THREE.HemisphereLight(0x2a5f86, 0x05090d, 0.7)
    this.scene.add(hemi)
    const key = new THREE.DirectionalLight(0xbfe2ff, 0.85)
    key.position.set(6, 14, 10)
    this.scene.add(key)
    // 芯片自发光用的补光，落在封装体上形成一个品牌蓝的高光边
    this.rimLight = new THREE.PointLight(PALETTE.brandLight, 1.35, 26)
    this.rimLight.position.set(0, 6.2, 3.2)
    this.scene.add(this.rimLight)
  }

  _initComposer () {
    // bloom 是这份"酷炫"里性价比最高的一环，但它是可选增强：
    // 一旦上下文丢失或不支持半浮点，回退成直接渲染，页面不能因此黑屏
    try {
      const { clientWidth: w, clientHeight: h } = this.container
      this.composer = new EffectComposer(this.renderer)
      this.composer.addPass(new RenderPass(this.scene, this.camera))
      this.bloomPass = new UnrealBloomPass(new THREE.Vector2(w || 800, h || 600), 0.78, 0.55, 0.62)
      this.composer.addPass(this.bloomPass)
      this.composer.setSize(w || 800, h || 600)
    } catch (e) {
      console.log('[dashboard3d] bloom composer unavailable, fallback to direct render')
      this.composer = null
      this.bloomPass = null
    }
  }

  // ── 场景内容 ────────────────────────────────────────────────

  _buildFloor () {
    this.floor = new THREE.Group()

    // 底盘：给网格一个承托，也让雾有由近及远的层次
    const plateGeo = new THREE.CircleGeometry(96, 64)
    const plateMat = new THREE.MeshStandardMaterial({
      color: PALETTE.chassis,
      roughness: 0.92,
      metalness: 0.12
    })
    const plate = new THREE.Mesh(plateGeo, plateMat)
    plate.rotation.x = -Math.PI / 2
    plate.position.y = -0.06
    this.floor.add(plate)

    const grid = new THREE.GridHelper(220, 110, PALETTE.brand, 0x14212e)
    grid.material.transparent = true
    grid.material.opacity = 0.22
    grid.material.depthWrite = false
    this.floor.add(grid)

    // 三道同心细环：低调的圈层，不抢柱状图的读数
    for (let i = 0; i < 3; i++) {
      const radius = 7.4 + i * 3.1
      const ringGeo = new THREE.RingGeometry(radius, radius + 0.025, 128)
      const ringMat = new THREE.MeshBasicMaterial({
        color: PALETTE.chrome,
        transparent: true,
        opacity: 0.06 + i * 0.015,
        side: THREE.DoubleSide,
        depthWrite: false
      })
      const ring = new THREE.Mesh(ringGeo, ringMat)
      ring.rotation.x = -Math.PI / 2
      ring.position.y = 0.01
      this.floor.add(ring)
    }

    // 日期刻度所在的轴圈：有了这一细圈，柱下那串日子读起来是“坐标轴”，不是散在地板上的字
    const axisRing = new THREE.Mesh(
      new THREE.RingGeometry(LABEL_RADIUS, LABEL_RADIUS + 0.02, 160),
      new THREE.MeshBasicMaterial({
        color: PALETTE.chrome,
        transparent: true,
        opacity: 0.05,
        side: THREE.DoubleSide,
        depthWrite: false
      })
    )
    axisRing.rotation.x = -Math.PI / 2
    axisRing.position.y = 0.01
    this.floor.add(axisRing)

    // 扫描脉冲环：从中心向外扩散，是"数据在流动"的信号
    this.pulse = new THREE.Mesh(
      new THREE.RingGeometry(1, 1.09, 96),
      new THREE.MeshBasicMaterial({
        color: PALETTE.brandLight,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
    )
    this.pulse.rotation.x = -Math.PI / 2
    this.pulse.position.y = 0.02
    this.floor.add(this.pulse)

    this.scene.add(this.floor)
  }

  _buildStars () {
    // 球壳上的散点，给暗背景一点体积感；单 draw call，几乎不吃性能
    const count = 1100
    const positions = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const r = 70 + Math.random() * 90
      const theta = Math.random() * Math.PI * 2
      // 只铺上半球，脚下留空，避免和地面网格打架
      const phi = Math.acos(Math.random() * 0.92)
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.cos(phi) + 4
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta)
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    const mat = new THREE.PointsMaterial({
      color: 0x9fc6e2,
      size: 0.55,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
    this.stars = new THREE.Points(geo, mat)
    this.scene.add(this.stars)
  }

  _buildChip () {
    // 中央模型：一颗通用的 MEMS 气体传感芯片，而不是某个具体型号的检测仪。
    // 机型外观有十几种，但芯片是所有型号共用的核心，换成它之后画面不再和某一台机器绑死。
    // 依旧全部用基础几何体 + CanvasTexture 程序化生成，不引入 glTF 资源与加载器。
    // 后续要换写实建模，把 _buildChip 换成 GLTFLoader + Draco 即可，其余不受影响。
    this.chip = new THREE.Group()
    this.chip.position.y = CHIP_Y
    const dummy = new THREE.Object3D()

    // 基座：芯片是悬在画面中央的，用一根锥形立柱把它接到地面上，不然会读不出重心
    const chromeMat = new THREE.MeshStandardMaterial({
      color: 0x24364a,
      metalness: 0.86,
      roughness: 0.28
    })
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 2.3, 0.32, 48), chromeMat)
    foot.position.y = 0.16
    this.scene.add(foot)
    const postH = CHIP_Y - 0.43
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.52, postH, 24), chromeMat)
    post.position.y = 0.3 + postH / 2
    this.scene.add(post)
    for (const y of [1.28, 2.42]) {
      const collar = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.045, 10, 32), chromeMat)
      collar.rotation.x = Math.PI / 2
      collar.position.y = y
      this.scene.add(collar)
    }

    const boardMat = new THREE.MeshStandardMaterial({
      color: 0x0c1721,
      metalness: 0.3,
      roughness: 0.62
    })
    const board = new THREE.Mesh(new THREE.BoxGeometry(4.4, 0.26, 4.4), boardMat)
    this.chip.add(board)

    // 板面的走线/焊盘/丝印用一张 CanvasTexture 画出来：比堆几何体便宜两个数量级
    this.boardTexture = this._makeBoardTexture()
    const faceMat = new THREE.MeshStandardMaterial({
      map: this.boardTexture,
      emissiveMap: this.boardTexture,
      emissive: PALETTE.brand,
      emissiveIntensity: 0.5,
      metalness: 0.34,
      roughness: 0.58
    })
    const face = new THREE.Mesh(new THREE.PlaneGeometry(4.4, 4.4), faceMat)
    face.rotation.x = -Math.PI / 2
    face.position.y = 0.14
    this.chip.add(face)

    // 封装体：开帽封装（cap-off），中间凹下去露出 die，气体才能直接接触到振膜
    const packageMat = new THREE.MeshStandardMaterial({
      color: 0x182634,
      metalness: 0.55,
      roughness: 0.42
    })
    const pack = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.34, 2.6), packageMat)
    pack.position.y = 0.3
    this.chip.add(pack)
    const rimMat = new THREE.MeshStandardMaterial({
      color: PALETTE.seam,
      metalness: 0.85,
      roughness: 0.22,
      emissive: PALETTE.brand,
      emissiveIntensity: 0.4
    })
    const rim = new THREE.Mesh(new THREE.BoxGeometry(2.74, 0.06, 2.74), rimMat)
    rim.position.y = 0.45
    this.chip.add(rim)
    const cavity = new THREE.Mesh(
      new THREE.BoxGeometry(1.52, 0.1, 1.52),
      new THREE.MeshStandardMaterial({ color: 0x0a1017, metalness: 0.2, roughness: 0.82 })
    )
    cavity.position.y = 0.47
    this.chip.add(cavity)

    const dieMat = new THREE.MeshStandardMaterial({
      color: 0x123044,
      metalness: 0.7,
      roughness: 0.24,
      emissive: PALETTE.brand,
      emissiveIntensity: 0.55
    })
    const die = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.1, 1.1), dieMat)
    die.position.y = 0.55
    this.chip.add(die)

    // die 上面一层加法混合的辉光片，随读数亮暗（原先是仪器屏幕的那块辉光）
    this.dieGlow = new THREE.Mesh(
      new THREE.PlaneGeometry(1.9, 1.9),
      new THREE.MeshBasicMaterial({
        color: PALETTE.brandLight,
        transparent: true,
        opacity: 0.2,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
    )
    this.dieGlow.rotation.x = -Math.PI / 2
    this.dieGlow.position.y = 0.63
    this.chip.add(this.dieGlow)

    const goldMat = new THREE.MeshStandardMaterial({
      color: 0xb08d4f,
      metalness: 0.92,
      roughness: 0.3
    })
    // 四边金手指引脚：一次 draw call，位置由角度推出来，避免手抄 40 组坐标
    const perSide = 10
    this.chipPins = new THREE.InstancedMesh(new THREE.BoxGeometry(0.12, 0.06, 0.72), goldMat, perSide * 4)
    for (let side = 0; side < 4; side++) {
      const theta = (side * Math.PI) / 2
      for (let i = 0; i < perSide; i++) {
        const point = this._chipPoint(theta, -1.17 + i * 0.26, 0.24, 1.66)
        dummy.position.copy(point)
        dummy.rotation.set(0, theta, 0)
        dummy.scale.set(1, 1, 1)
        dummy.updateMatrix()
        this.chipPins.setMatrixAt(side * perSide + i, dummy.matrix)
      }
    }
    this.chip.add(this.chipPins)

    // 键合线：die 边缘拉到引脚内侧的金线，TubeGeometry 沿二次贝塞尔曲线扫出来
    for (let side = 0; side < 4; side++) {
      const theta = (side * Math.PI) / 2
      for (let i = 0; i < 3; i++) {
        const curve = new THREE.QuadraticBezierCurve3(
          this._chipPoint(theta, -0.36 + i * 0.36, 0.6, 0.55),
          this._chipPoint(theta, -0.18 + i * 0.36, 0.94, 0.94),
          this._chipPoint(theta, -0.65 + i * 0.52, 0.27, 1.3)
        )
        const wire = new THREE.Mesh(new THREE.TubeGeometry(curve, 18, 0.017, 5, false), goldMat)
        this.chip.add(wire)
      }
    }

    // 振膜微结构：die 上一圈六角环阵，是画面里唯一会随 Bloom 呼吸的自发光细节
    const cellMat = new THREE.MeshBasicMaterial({
      color: PALETTE.brandLight,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    })
    const spots = []
    for (let r = -2; r <= 2; r++) {
      for (let c = -2; c <= 2; c++) {
        if (r * r + c * c <= 4) spots.push([r * 0.15, c * 0.15])
      }
    }
    this.chipCells = new THREE.InstancedMesh(new THREE.RingGeometry(0.028, 0.052, 6), cellMat, spots.length)
    spots.forEach((spot, i) => {
      dummy.position.set(spot[0], 0.61, spot[1])
      dummy.rotation.set(-Math.PI / 2, 0, 0)
      dummy.scale.set(1, 1, 1)
      dummy.updateMatrix()
      this.chipCells.setMatrixAt(i, dummy.matrix)
    })
    this.chip.add(this.chipCells)

    // 双陀螺环：一正一反转，是画面里最主要的"活"的元素
    // 半径必须避开板面的方形（边 2.2、角 3.11），否则环会被板子吃掉一段
    const haloMat = new THREE.MeshBasicMaterial({
      color: PALETTE.brandLight,
      transparent: true,
      opacity: 0.42,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
    this.haloA = new THREE.Mesh(new THREE.TorusGeometry(3.35, 0.018, 8, 160), haloMat)
    this.haloA.rotation.x = Math.PI / 2
    this.haloA.position.y = 0.34
    this.chip.add(this.haloA)
    this.haloB = new THREE.Mesh(new THREE.TorusGeometry(2.95, 0.014, 8, 160), haloMat)
    this.haloB.rotation.set(Math.PI / 2.6, 0, 0.42)
    this.haloB.position.y = 0.3
    this.chip.add(this.haloB)

    // 底座光晕：让立柱"压"在地面上，而不是浮着
    const base = new THREE.Mesh(
      new THREE.CircleGeometry(3.2, 64),
      new THREE.MeshBasicMaterial({
        color: PALETTE.brand,
        transparent: true,
        opacity: 0.16,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
    )
    base.rotation.x = -Math.PI / 2
    base.position.y = 0.03
    this.scene.add(base)

    this.scene.add(this.chip)
  }

  /** 把"某一条边上的局部坐标"旋到芯片坐标：四边引脚和键合线共用一套写法 */
  _chipPoint (theta, x, y, z) {
    const sin = Math.sin(theta)
    const cos = Math.cos(theta)
    return new THREE.Vector3(x * cos + z * sin, y, -x * sin + z * cos)
  }

  /** PCB 板面：走线、过孔、丝印都画在一张 512 的 canvas 上 */
  _makeBoardTexture () {
    const size = 512
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const center = size / 2
    const pad = 150 // 封装区半宽，和封装体 2.6 的实际尺寸对齐（1px ≈ 0.0086 场景单位）

    ctx.fillStyle = '#0c1721'
    ctx.fillRect(0, 0, size, size)

    // 覆铜网格：让板子不至于是一片死黑，斜看有细密纹理
    ctx.strokeStyle = 'rgba(22,35,47,0.9)'
    ctx.lineWidth = 1
    for (let i = 0; i <= size; i += 16) {
      ctx.beginPath()
      ctx.moveTo(i, 0)
      ctx.lineTo(i, size)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(0, i)
      ctx.lineTo(size, i)
      ctx.stroke()
    }

    // 四边各 10 条走线：出封装区先斜折一次再直冲板边，末尾落一个焊盘
    ctx.lineCap = 'round'
    ctx.save()
    ctx.translate(center, center)
    for (let side = 0; side < 4; side++) {
      ctx.save()
      ctx.rotate((side * Math.PI) / 2)
      for (let i = 0; i < 10; i++) {
        const x = -117 + i * 26
        ctx.strokeStyle = 'rgba(56,182,240,0.5)'
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.moveTo(x, -pad)
        ctx.lineTo(x - 10, -pad - 26)
        ctx.lineTo(x - 10, -200)
        ctx.stroke()
        ctx.fillStyle = 'rgba(176,141,79,0.85)'
        ctx.beginPath()
        ctx.arc(x - 10, -196, 4.5, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
    }

    // 丝印：板边一眼能读出这是颗什么器件
    ctx.fillStyle = 'rgba(143,165,184,0.72)'
    ctx.textAlign = 'center'
    ctx.font = 'bold 28px monospace'
    ctx.fillText('MEMS', 0, -232)
    ctx.font = '18px monospace'
    ctx.fillText('GAS SENSOR', 0, 246)
    // 封装区丝印框 + 一号脚标记
    ctx.strokeStyle = 'rgba(143,165,184,0.4)'
    ctx.lineWidth = 2
    ctx.strokeRect(-pad - 12, -pad - 12, (pad + 12) * 2, (pad + 12) * 2)
    ctx.fillStyle = 'rgba(176,141,79,0.9)'
    ctx.beginPath()
    ctx.arc(-pad - 42, -pad - 42, 9, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()

    const texture = new THREE.CanvasTexture(canvas)
    texture.anisotropy = 4
    return texture
  }

  _buildGas () {
    // 气体分子从芯片上方四周的壳层向 die 汇聚，被振膜"吸"进去：
    // 读数越高越密越快，颜色跟着 pass/warn/fail 走（原先后置的吹气柱语义反过来）
    const positions = new Float32Array(GAS_COUNT * 3)
    this.gasData = []
    for (let i = 0; i < GAS_COUNT; i++) {
      this.gasData.push(this._respawnGas({}, true))
      this._writeGas(positions, i, this.gasData[i])
    }
    const geo = new THREE.BufferGeometry()
    this.gasAttr = new THREE.BufferAttribute(positions, 3)
    geo.setAttribute('position', this.gasAttr)
    this.gasMat = new THREE.PointsMaterial({
      color: PALETTE.pass,
      size: 0.22,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
    this.gas = new THREE.Points(geo, this.gasMat)
    this.gas.geometry.setDrawRange(0, GAS_COUNT)
    this.scene.add(this.gas)

    // 吸附点的一团聚光，随读数变色，是"当前状态"最直接的信号
    this.senseGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.42, 20, 16),
      new THREE.MeshBasicMaterial({
        color: PALETTE.pass,
        transparent: true,
        opacity: 0.45,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
    )
    this.senseGlow.position.copy(GAS_TARGET)
    this.scene.add(this.senseGlow)

    this.gasIntensity = 0.22 // 空闲时也有微弱分子活动，画面不能是死的
    this.gasTone = PALETTE.pass
  }

  _respawnGas (state, spread) {
    // 初始一次撒满全程（spread），之后每个粒子寿命到期就地重生
    const t = spread ? Math.random() : 0
    state.angle = Math.random() * Math.PI * 2
    state.radius = 1.9 + Math.random() * 3.4 // 出生时离中心的远近
    state.height = GAS_TARGET.y + 0.7 + Math.random() * 4.2 // 出生时的高度
    state.swirl = 0.6 + Math.random() * 1.4 // 吸附过程中绕中心的圈数
    state.life = 1.6 + Math.random() * 1.5
    state.age = t * state.life
    return state
  }

  _writeGas (arr, i, state) {
    // 直线插值看着像减速降落，用幂函数缓入：越靠近芯片越快，才读得出"被吸附"
    const k = Math.min(1, state.age / state.life)
    const ease = Math.pow(k, 1.7)
    const radius = state.radius * (1 - ease) + 0.06
    const angle = state.angle + ease * state.swirl
    arr[i * 3] = Math.cos(angle) * radius
    arr[i * 3 + 1] = state.height + (GAS_TARGET.y - state.height) * ease
    arr[i * 3 + 2] = Math.sin(angle) * radius
  }

  _buildBars () {
    const barMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.4,
      roughness: 0.38
    })
    // 单位高度立方体，靠 instance 的 yScale 决定柱高：一次 draw call 画完 30 根
    // 柱体宽度跟着 60→30 根一起放宽：30 天在 RING_RADIUS 上间隔接近 2.8，
    // 沿用 0.66 的话环会稀成“一排牙签”，反而比 60 根时更不像一个整体
    this.bars = new THREE.InstancedMesh(new THREE.BoxGeometry(0.95, 1, 0.95), barMat, MAX_BARS)
    this.bars.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.bars.count = 0
    this.scene.add(this.bars)

    // 柱顶光帽：bloom 只吃得到这里，是环形统计"亮起来"的关键
    this.barCaps = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1.05, 0.07, 1.05),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      }),
      MAX_BARS
    )
    this.barCaps.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.barCaps.count = 0
    this.scene.add(this.barCaps)

    // 当日标记：悬在今天的柱子之上，缓慢上下浮动
    this.marker = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.34),
      new THREE.MeshBasicMaterial({
        color: PALETTE.brandLight,
        transparent: true,
        opacity: 0.92,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
    )
    this.marker.visible = false
    this.scene.add(this.marker)

    this._dummy = new THREE.Object3D()
    this._color = new THREE.Color()
    this._buildBarLabels()
  }

  _buildBarLabels () {
    // 柱下方一圈日期刻度。用 billboard sprite而不是贴在地板上的平面：
    // 环形布局下任何“平铺”的写法都会有一半日子是倒着读的，sprite 始终正对相机
    this._labelCache = {}
    this.barLabels = []
    for (let i = 0; i < MAX_BARS; i++) {
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        transparent: true,
        opacity: 0.82,
        depthWrite: false
      }))
      sprite.scale.set(LABEL_W, LABEL_H, 1)
      sprite.visible = false
      this.scene.add(sprite)
      this.barLabels.push(sprite)
    }
  }

  _dateTexture (text) {
    // 纹理按日期字符串缓存：轮询每 60s 重喂一次 30 个日子，实际每天只会多出一张新纹理
    const cache = this._labelCache
    if (cache[text]) return cache[text]
    // 512×128 是算过的：环背面的刻度离相机约 30~40，文字在屏上只剩十几个像素，
    // 画布再小下去，缩小采样加 mipmap 就会把笔画浑成一片
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 128
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, 512, 128)
    ctx.font = 'bold 62px monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.lineJoin = 'round'
    // 靠深色描边把字从地面网格和柱体暗部里剔出来，而不是把字调白：
    // 高亮度的白字会被 bloom 抹开成一团光晕，反而更认不出
    ctx.lineWidth = 14
    ctx.strokeStyle = 'rgba(5,10,16,0.88)'
    ctx.strokeText(text, 256, 66)
    ctx.fillStyle = 'rgba(222,238,250,0.98)'
    ctx.fillText(text, 256, 66)
    const texture = new THREE.CanvasTexture(canvas)
    // 刻度面与视线斜交，不用 anisotropy 的话远侧那一排会被采样糊掉
    texture.anisotropy = 8
    cache[text] = texture
    return texture
  }

  _placeBarLabel (index, angle, text) {
    const sprite = this.barLabels[index]
    if (!sprite) return
    const texture = this._dateTexture(text)
    if (sprite.material.map !== texture) {
      sprite.material.map = texture
      // 换 map 不标 needsUpdate，three 不会重传纹理，看到的还是上一次那个日子
      sprite.material.needsUpdate = true
    }
    // 中点抬到 0.45：sprite 变高后按原来的 0.34 会有一截扎进地板
    sprite.position.set(Math.cos(angle) * LABEL_RADIUS, 0.45, Math.sin(angle) * LABEL_RADIUS)
    sprite.visible = true
  }

  // 选中那根柱的刻度用品牌蓝顶到最亮，其余压一档：环形上“现在看的是哪一天”要能一眼对上
  _refreshLabelEmphasis () {
    if (!this.barLabels) return
    for (let i = 0; i < this.barLabels.length; i++) {
      const sprite = this.barLabels[i]
      if (!sprite.visible) continue
      const active = i === this.highlightIndex
      sprite.material.opacity = active ? 1 : 0.82
      sprite.material.color.setHex(active ? PALETTE.brandLight : 0xffffff)
    }
  }

  // ── 对外数据接口 ────────────────────────────────────────────

  /**
   * @param {Array} series [{ date, testUNum, totalUNum, limitedUser, percent }] 按日期升序
   */
  setSeries (series) {
    const list = (series || []).slice(-MAX_BARS)
    const dummy = this._dummy
    const color = this._color
    const toneBase = new THREE.Color(PALETTE.brand)
    this.barPolar = []
    this.barHeights = []
    this.barDates = list.map(item => item.date)

    let max = 1
    for (const item of list) {
      max = Math.max(max, item.testUNum || 0)
    }

    for (let i = 0; i < list.length; i++) {
      const item = list[i]
      const angle = (i / list.length) * Math.PI * 2 - Math.PI / 2
      this.barPolar.push(angle)
      const value = item.testUNum || 0
      const height = 0.5 + (value / max) * 6.8
      this.barHeights.push(height)
      dummy.position.set(Math.cos(angle) * RING_RADIUS, height / 2, Math.sin(angle) * RING_RADIUS)
      dummy.rotation.set(0, -angle, 0)
      dummy.scale.set(1, height, 1)
      dummy.updateMatrix()
      this.bars.setMatrixAt(i, dummy.matrix)

      dummy.position.y = height + 0.05
      dummy.scale.set(1, 1, 1)
      dummy.updateMatrix()
      this.barCaps.setMatrixAt(i, dummy.matrix)

      // 有超标日的柱子偏向语义色，没检测的日子压成接近地面的暗蓝
      const ratio = item.totalUNum ? (item.limitedUser || 0) / item.totalUNum : 0
      if (value <= 0) {
        color.setHex(PALETTE.seam)
      } else if (ratio > 0.08) {
        color.setHex(PALETTE.fail).lerp(toneBase, 0.35)
      } else if (ratio > 0) {
        color.setHex(PALETTE.warn).lerp(toneBase, 0.45)
      } else {
        color.setHex(PALETTE.brand)
      }
      this.bars.setColorAt(i, color)
      this.barCaps.setColorAt(i, color)
      // 刻度跟柱子同一个 angle，不能自己再算一遍分布
      this._placeBarLabel(i, angle, item.label || item.date)
    }

    // 日子不够 30 个时把池子里多出来的刻度收掉
    for (let i = list.length; i < this.barLabels.length; i++) {
      this.barLabels[i].visible = false
    }

    this.bars.count = list.length
    this.barCaps.count = list.length
    this.bars.instanceMatrix.needsUpdate = true
    this.barCaps.instanceMatrix.needsUpdate = true
    if (this.bars.instanceColor) this.bars.instanceColor.needsUpdate = true
    if (this.barCaps.instanceColor) this.barCaps.instanceColor.needsUpdate = true
    this.bars.visible = list.length > 0
    this.barCaps.visible = list.length > 0
    // 标记跟着"选中的那一天"走而不是下标：30 天窗口每天会前移一格，
    // 按日期找回才能让轮刷新不把用户点选的日子抢回今天
    let target = list.length - 1
    if (this.highlightDate) {
      const found = list.findIndex(item => item.date === this.highlightDate)
      if (found >= 0) target = found
    }
    this.setHighlight(target)
  }

  /**
   * 当前读数驱动气体分子：强度决定密度与吸附速度，等级决定颜色
   * @param {number} intensity 0~1
   * @param {string} tone 'pass' | 'warn' | 'fail'
   */
  setGas (intensity, tone) {
    const value = Math.max(0, Math.min(1, Number(intensity) || 0))
    this.gasIntensity = 0.18 + value * 0.82
    this.gasTone = PALETTE[tone] || PALETTE.pass
    this.gasMat.color.setHex(this.gasTone)
    this.senseGlow.material.color.setHex(this.gasTone)
    // 密度用 drawRange 控制，比重建 buffer 便宜两个数量级
    const drawn = Math.round(GAS_COUNT * (0.12 + value * 0.88))
    this.gas.geometry.setDrawRange(0, drawn)
    this.senseGlow.material.opacity = 0.28 + value * 0.5
    this.dieGlow.material.opacity = 0.16 + value * 0.28
  }

  /** 高亮某一根柱（默认今天）；index 越界或无数据则收起标记 */
  setHighlight (index) {
    const angle = this.barPolar[index]
    if (typeof angle === 'undefined') {
      this.marker.visible = false
      this.highlightIndex = -1
      // 无选中也要重扫一遍刻度，否则整圈会停在上一轮那个亮着的日子上
      this._refreshLabelEmphasis()
      return
    }
    this.highlightIndex = index
    this.highlightDate = (this.barDates && this.barDates[index]) || null
    this.marker.visible = true
    // 标记悬在选中柱顶上方 1.2，柱子高低变了它也跟着变
    this.markerBaseY = (this.barHeights[index] || 1) + 1.2
    this.marker.position.set(Math.cos(angle) * RING_RADIUS, this.markerBaseY, Math.sin(angle) * RING_RADIUS)
    this._refreshLabelEmphasis()
  }

  /** 页面切到后台时调用，省 GPU 也省电；回到前台再恢复 */
  setPaused (paused) {
    this.paused = !!paused
  }

  resize () {
    this._resize()
  }

  // ── 事件与循环 ──────────────────────────────────────────────

  _bindEvents () {
    this._onResize = () => this._resize()
    window.addEventListener('resize', this._onResize)
    // 容器先于窗口变尺寸的场景（侧栏折叠、布局里的高度重算、页面缩放）靠 ResizeObserver 兜住，
    // 单靠 window resize 会漏掉这些不改变视口的变化
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(this._onResize)
      this._resizeObserver.observe(this.container)
    }
    // 后台标签里窗口尺寸变了不会触发 resize，回到前台先对一次，否则画面会停在旧尺寸
    this._onVisibility = () => {
      if (!document.hidden) this._resize()
    }
    document.addEventListener('visibilitychange', this._onVisibility)
    const el = this.renderer.domElement
    this._onDown = (e) => {
      this.pointer.down = { x: e.clientX, y: e.clientY }
      this.pointer.moved = false
    }
    this._onMove = (e) => {
      if (this.pointer.down &&
        (Math.abs(e.clientX - this.pointer.down.x) > 6 || Math.abs(e.clientY - this.pointer.down.y) > 6)) {
        this.pointer.moved = true
      }
    }
    this._onUp = (e) => {
      if (this.pointer.down && !this.pointer.moved) {
        this._pick(e)
      }
      this.pointer.down = null
    }
    el.addEventListener('pointerdown', this._onDown)
    el.addEventListener('pointermove', this._onMove)
    el.addEventListener('pointerup', this._onUp)
  }

  /** 点柱选日：移动超过阈值视为拖拽视角，不触发选日 */
  _pick (event) {
    if (!this.bars || this.bars.count === 0) return
    const rect = this.renderer.domElement.getBoundingClientRect()
    this._ray = this._ray || new THREE.Raycaster()
    this._ndc = this._ndc || new THREE.Vector2()
    this._ndc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    )
    this._ray.setFromCamera(this._ndc, this.camera)
    const hit = this._ray.intersectObject(this.bars, false)[0]
    if (!hit || typeof hit.instanceId !== 'number') return
    this.setHighlight(hit.instanceId)
    if (this.hooks.onSelectDay) {
      this.hooks.onSelectDay(hit.instanceId)
    }
  }

  _resize () {
    const w = this.container.clientWidth
    const h = this.container.clientHeight
    if (!w || !h) return
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(w, h)
    if (this.composer) this.composer.setSize(w, h)
  }

  _loop () {
    if (this.disposed) return
    this.raf = requestAnimationFrame(() => this._loop())
    const delta = Math.min(this.clock.getDelta(), 0.05)
    const now = performance.now()
    // 帧率闸门：低于目标帧间隔直接跳过，循环本身仍在跑但几乎不消耗
    if (now - this.lastFrame < 1000 / this.fps) return
    this.lastFrame = now
    if (this.paused || document.hidden) return
    this._update(delta, now / 1000)
    this._render()
  }

  _update (delta, time) {
    this.controls.update()
    this.chip.rotation.y += delta * 0.14
    this.haloA.rotation.z += delta * 0.55
    this.haloB.rotation.z -= delta * 0.38
    this.stars.rotation.y += delta * 0.008

    // 扫描脉冲：扩散到位后重置，形成一个 3.2s 的呼吸周期
    const phase = (time % 3.2) / 3.2
    const scale = 1 + phase * 12.5
    this.pulse.scale.set(scale, scale, scale)
    this.pulse.material.opacity = 0.42 * (1 - phase) * (1 - phase)

    this._updateGas(delta)

    if (this.marker.visible) {
      this.marker.rotation.y += delta * 1.2
      this.marker.position.y = this.markerBaseY + Math.sin(time * 1.6) * 0.24
    }
  }

  _updateGas (delta) {
    const attr = this.gas.geometry.attributes.position
    const arr = attr.array
    const drawn = this.gas.geometry.drawRange.count
    const power = this.gasIntensity
    for (let i = 0; i < drawn; i++) {
      const state = this.gasData[i]
      // 读数越高流动越快，不只是粒子变多
      state.age += delta * (0.6 + power * 0.9)
      if (state.age >= state.life) {
        this._respawnGas(state, false)
      }
      this._writeGas(arr, i, state)
    }
    attr.needsUpdate = true
    const glowScale = 0.7 + power * (1.1 + Math.sin(performance.now() / 260) * 0.18)
    this.senseGlow.scale.set(glowScale, glowScale, glowScale)
    // 振膜跟着吸附节律轻微明暗，让芯片看着确实在"听"这些分子
    this.chipCells.material.opacity = 0.34 + power * (0.24 + Math.sin(performance.now() / 420) * 0.08)
  }

  _render () {
    if (this.composer) {
      this.composer.render()
    } else {
      this.renderer.render(this.scene, this.camera)
    }
  }

  // ── 释放 ────────────────────────────────────────────────────

  dispose () {
    if (this.disposed) return
    this.disposed = true
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this._labelCache) {
      // sprite 的 material 由下面的 scene.traverse 负责，但纹理得显式收，
      // 30 张 256×64 的刻度图不释放的话，切几次路由就会攒下一堆显存
      Object.keys(this._labelCache).forEach((key) => this._labelCache[key].dispose())
      this._labelCache = {}
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    window.removeEventListener('resize', this._onResize)
    document.removeEventListener('visibilitychange', this._onVisibility)
    const el = this.renderer && this.renderer.domElement
    if (el) {
      el.removeEventListener('pointerdown', this._onDown)
      el.removeEventListener('pointermove', this._onMove)
      el.removeEventListener('pointerup', this._onUp)
    }
    if (this.controls) this.controls.dispose()
    if (this.scene) {
      // SPA 里切几次路由不清 GPU 资源，显存会一路涨到崩，这里逐个 geometry/material 收
      this.scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose()
        const material = obj.material
        if (!material) return
        if (Array.isArray(material)) {
          material.forEach((m) => m.dispose())
        } else {
          material.dispose()
        }
      })
    }
    if (this.bars) this.bars.dispose()
    if (this.barCaps) this.barCaps.dispose()
    if (this.chipPins) this.chipPins.dispose()
    if (this.chipCells) this.chipCells.dispose()
    // material.dispose() 不会连带释放 texture，板面那张 CanvasTexture 要单独收
    if (this.boardTexture) this.boardTexture.dispose()
    if (this.composer) this.composer.renderTarget1.dispose()
    if (this.renderer) {
      this.renderer.dispose()
      if (this.renderer.forceContextLoss) this.renderer.forceContextLoss()
      if (el && el.parentNode) el.parentNode.removeChild(el)
    }
    this.scene = null
    this.camera = null
  }
}
